import { Project } from '../shared/project/project';
import { ProjectRow } from '../shared/project/project-row';
import { Reveal } from '../shared/reveal/reveal';
import { ProjectProps } from '../shared/project/contracts/project-props';
import stringLogo from '../shared/assets/string-generator/dark.png';
import iotLogo from '../shared/assets/iot-web/iot.png';
import spaceLaunches from '../shared/assets/space-launches/launches.png';
import spaceLaunchesReact from '../shared/assets/space-launches/react/dark.png';

// Web apps — screenshot-backed sites, shown as image cards.
const WEB_APPS: ProjectProps[] = [
  {
    id: 'random-string-generator',
    title: 'Random String Generator',
    excerpt: 'A simple React site for generating randomized strings.',
    description:
      "A small site for generating randomized strings, mostly an excuse to experiment with React. Most of my professional experience is in Angular, so I built this as a low-stakes place to get hands-on with React's component model and hooks on something real but simple.",
    logo: stringLogo,
    link: 'https://string-generator.gardnernick.com/',
  },
  {
    id: 'iot-portal',
    title: 'IoT Portal',
    excerpt: 'A portal for monitoring and controlling my home IoT projects.',
    description:
      "A hub-style web app for the IoT devices I've built around the house, like the automated dog water bowl and the garage door monitor. Instead of a separate interface per device, the portal gives me one place to check status and trigger actions, talking to each project's backend from a single dashboard.",
    logo: iotLogo,
    link: 'https://iot.gardnernick.com',
  },
  {
    id: 'space-launches',
    title: 'Space Launches',
    excerpt: 'An Angular site tracking upcoming NASA and SpaceX launches.',
    description:
      'A site that surfaces upcoming NASA and SpaceX launches, backed by an automated web scraper I built to collect the launch data rather than relying on a public API. This was the original Angular build, showing the upcoming launch schedule. I later rebuilt it in React (see Space Launches (React)) to compare the two frameworks.',
    logo: spaceLaunches,
  },
  {
    id: 'space-launches-react',
    title: 'Space Launches (React)',
    excerpt: 'A React rebuild of Space Launches, now with past launches too.',
    description:
      'The React rebuild of my original Angular Space Launches site. Beyond porting the upcoming-launch view, I expanded it to include previous launches, so you can browse both the schedule ahead and the history of past NASA and SpaceX missions. Building the same app twice made the differences between Angular and React concrete.',
    logo: spaceLaunchesReact,
    link: 'https://space-launches.com',
  },
];

// Hardware & tooling — no screenshot, shown as compact terminal rows.
const LAB: ProjectProps[] = [
  {
    id: 'dog-bowl',
    title: 'Dog Water Bowl',
    tag: 'IoT',
    excerpt: 'An ESP32 water bowl that keeps my two dogs topped up.',
    description:
      'An ESP32-driven water bowl for my two dogs. A manually filled reservoir pumps water into the bowl on a schedule I set in the IoT Portal, and I can also trigger a fill on demand from the site. When the reservoir runs low it sends me an email and shows a notification badge on the site so I know to refill it.',
  },
  {
    id: 'garage-door',
    title: 'Garage Door Monitor',
    tag: 'IoT',
    excerpt: "An ESP32 that alerts me when the garage door's left open.",
    description:
      'An ESP32-based sensor that watches whether my garage door is open or closed and reports the state to my IoT Portal in real time. If the door stays open past a threshold I configure in the portal, it sends me an email alert and flags it on the site.',
  },
  {
    id: 'static-website-builder',
    title: 'Static Website Builder',
    tag: 'npm',
    excerpt: 'An npm CDK construct for deploying static sites to AWS.',
    description:
      'A published npm package (an AWS CDK construct) that stands up everything a static site needs on AWS (S3, CloudFront, and Route 53) in a few lines of code. Paired with a GitHub Actions workflow, it turns "deploy a new static site" into a repeatable, one-command process. It\'s what hosts this very site.',
    link: 'https://github.com/gardner-nick/cdk-static-website-package',
  },
  {
    id: 'blog-backend',
    title: 'Blog Backend',
    tag: 'npm',
    excerpt: 'An npm CDK construct for a serverless blog backend on AWS.',
    description:
      "The blog counterpart to my static-site construct: another published npm package (an AWS CDK construct), this one for a full serverless blog backend on AWS. It's an API Gateway HTTP API in front of a single Lambda router, backed by DynamoDB, with an optional S3 bucket for image uploads via presigned URLs. It ships public read routes for posts and comments plus authorizer-protected admin routes for writing, so a blog is just a few lines of CDK. It's what powers the blog on this site.",
    link: 'https://github.com/gardner-nick/cdk-blog-backend-package',
  },
];

function GroupLabel({ path }: { path: string }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <span className="font-mono text-sm text-muted">
        <span className="text-accent">~</span>/{path}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

export function Websites() {
  return (
    <section id="projects" className="mx-auto max-w-content px-5 py-16">
      <Reveal>
        <p className="section-kicker">{'// 03 — projects'}</p>
        <h2 className="mt-2 font-mono text-2xl font-bold tracking-tight text-fg sm:text-3xl">
          Selected projects
        </h2>
      </Reveal>

      <Reveal className="mt-8">
        <GroupLabel path="web-apps" />
      </Reveal>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {WEB_APPS.map((project, i) => (
          <Reveal key={project.id} delay={(i % 3) * 80}>
            <Project {...project} />
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-12">
        <GroupLabel path="hardware-and-tools" />
        <div className="terminal-card divide-y divide-border overflow-hidden">
          {LAB.map((project) => (
            <ProjectRow key={project.id} {...project} />
          ))}
        </div>
      </Reveal>
    </section>
  );
}
